import { useContext, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { useAnalytics } from '@/hooks/analytics';

import AnimatedScreenWrapper from '../../Components/AnimatedScreenWrapper';
import WelcomeTooltip from '../../Components/WelcomeTooltip';
import { PreSignupFlowContext } from '../../PreSignupProvider';
import { DivElementRef } from '../../PreSignupProvider/types';
import messages from './messages';
import { Heading, Logo, OnboardingButtons, TopContent, WelcomeMessage, Wrapper } from './styles';

interface Props {
	compact?: boolean;
	parentRef?: DivElementRef;
}

const TransitionScreen = ({ compact, parentRef }: Props) => {
	const { nextScreen, skipToEnd, ActiveScreen } = useContext(PreSignupFlowContext);
	const { transitionType, analyticsName } = ActiveScreen;
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	async function onContinue(): Promise<void> {
		logEvent({
			eventName: 'FTUE : Transition : Proceeded',
			eventProps: {
				transition_type: transitionType as string,
			},
		});
		nextScreen();
	}

	async function onCustomSkip(): Promise<void> {
		logEvent({
			eventName: `${analyticsName} : Skipped`,
			eventProps: {
				transition_type: transitionType as string,
			},
		});
		if (parentRef) {
			parentRef.current?.scrollIntoView({ behavior: 'smooth' });
		} else {
			window.scrollTo(0, 0);
		}
		skipToEnd();
	}

	return (
		<AnimatedScreenWrapper>
			<Wrapper>
				<TopContent>
					<Heading tabIndex={-1}>
						<WelcomeMessage>
							{formatMessage(messages.welcome, {
								Calm: <Logo src="/_n/images/calm-logo-gradient.png" alt="Calm" />,
							})}
						</WelcomeMessage>
					</Heading>
					<p>
						{formatMessage(messages.congratulations, {
							bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
						})}
					</p>
					<p>
						{formatMessage(messages.getStarted, {
							bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
						})}
					</p>
				</TopContent>
				<WelcomeTooltip />
				<OnboardingButtons
					onContinue={onContinue}
					onCustomSkip={onCustomSkip}
					continueButtonStyle={{
						backgroundColor: 'white',
						textColor: 'gray7',
					}}
					compact={compact}
				/>
			</Wrapper>
		</AnimatedScreenWrapper>
	);
};

export default TransitionScreen;
